import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Link,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Code,
  FormatAlignLeft,
  LooksOne,
  PlaylistAddCheck,
  Category,
} from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";

const TemplateBodyInstructions = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleOpen();
          }}
          style={{
            ...typography.bodyLarge,
            textDecoration: "none",
            cursor: "pointer",
            color: "#367EE2",
            fontWeight: "bold",
          }}
        >
          View Message Body Guidelines
        </a>
      </Typography>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="parameter-guide-dialog"
      >
        <DialogTitle
          id="parameter-guide-dialog"
          sx={{ borderBottom: "1px solid #e0e0e0" }}
        >
          <Typography variant="h5" component="h2" fontWeight="bold">
            Message Body Guidelines
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Code color="primary" />
              Parameter Syntax
            </Typography>
            <Typography variant="body2" sx={{ pl: 4 }}>
              Add dynamic values using <strong>{`{{i}}`}</strong>, where i is
              the parameter number.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                pl: 4,
                mt: 1,
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                p: 1,
                borderRadius: 1,
              }}
            >
              Example: {`{{1}}`}, {`{{2}}`}, etc.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <FormatAlignLeft color="primary" />
              Parameter Placement
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="Each parameter must have text before and after it."
                  secondary='Example: "Hello {{1}}, how are you?"'
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary="The body cannot end with a parameter."
                  secondary={
                    <>
                      <Typography variant="body2" color="error">
                        Incorrect: "Hello {`{{1}}`}!"
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Correct: "Hello {`{{1}}`} user!"
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <LooksOne color="primary" />
              Parameter Uniqueness
            </Typography>
            <Typography variant="body2" sx={{ pl: 4 }}>
              Each parameter number can be used only once in the body. You
              cannot repeat {`{{1}}`} multiple times.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <PlaylistAddCheck color="primary" />
              Parameter Values
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>•</ListItemIcon>
                <ListItemText primary="Parameter options come from your Audience attributes (e.g., Name, Email, Gender, etc.)." />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>•</ListItemIcon>
                <ListItemText primary='To use your own value, select "Custom" and enter it in the textbox.' />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Category color="primary" />
              Utility Template Specific
            </Typography>
            <Typography variant="body2" sx={{ pl: 4 }}>
              When submitting a template for the Utility category, the content
              must reflect a utility purpose.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                pl: 4,
                mt: 1,
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                p: 1,
                borderRadius: 1,
              }}
            >
              Example: Hello User, your loan is approved for {`{{1}}`}. Please
              reach out for further procedure.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
          <ButtonOne
            onClick={handleClose}
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
          >
            Got It
          </ButtonOne>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateBodyInstructions;
